import {
  Box,
  Grid,
  Typography,
  IconButton,
  TextField,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  Snackbar,
  Alert,
  styled,
  Backdrop,
  CircularProgress
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usersWithUpcomingBday } from '../../actions/admin';
import { downloadInExcel } from '../../utils';
import { clearUpcomingBdays } from '../../utils/store/slice/adminSlice';
import { Cake, Download, CalendarToday, FileDownload } from '@mui/icons-material';

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  paddingTop: '2rem',
  paddingBottom: '2rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease-in-out',
  padding: '2rem',
  maxWidth: '500px',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    '& fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(103, 126, 234, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#667eea',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#667eea',
    fontWeight: '500',
  },
  '& input': {
    height: '40px',
    padding: '0px 14px !important',
    fontSize: '1rem',
  }
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: '600',
  boxShadow: '0 4px 15px rgba(103, 126, 234, 0.4)',
  transition: 'all 0.3s ease',
  minWidth: '180px',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(103, 126, 234, 0.6)',
  },
  '&:disabled': {
    background: 'rgba(103, 126, 234, 0.3)',
    color: 'rgba(255, 255, 255, 0.7)',
  }
}));

const Label = styled(Typography)({
  marginBottom: '12px',
  fontWeight: '600',
  color: '#374151',
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: 'white',
  fontWeight: '700',
  marginBottom: '2rem',
  textAlign: 'center',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  position: 'absolute',
  top: '2rem',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
}));

const Birthday = () => {
  const { upcomingBdays } = useSelector(state => state.adminSlice);
  const dispatch = useDispatch();
  const [days_left, setDaysLeft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const getData = () => {
    if (!days_left || days_left < 0) {
      setToast({ open: true, message: 'Please enter a valid number of days', severity: 'warning' });
      return;
    }

    setLoading(true);
    dispatch(usersWithUpcomingBday({ days_left }));
  };

  const downloadData = async () => {
    try {
      const data = upcomingBdays;
      downloadInExcel({
        data,
        fileName: `bday-after-${days_left}-days`
      });
      setToast({ open: true, message: `Birthday data downloaded successfully!`, severity: 'success' });
      setDaysLeft(null);
    } catch (error) {
      setToast({ open: true, message: 'Failed to download data', severity: 'error' });
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      getData();
    }
  };

  useEffect(() => {
    if (upcomingBdays?.length) {
      downloadData();
    } else {
      if (days_left !== null && loading) {
        setToast({ open: true, message: `No birthdays found in the next ${days_left} days.`, severity: 'info' });
        setDaysLeft(null);
        setLoading(false);
      }
    }
  }, [upcomingBdays]);

  useEffect(() => {
    if (days_left === null) dispatch(clearUpcomingBdays());
  }, [days_left]);

  return (
    <StyledContainer>
      <SectionTitle variant='h4'>
        <Cake sx={{ mr: 1, verticalAlign: 'middle' }} />
        Birthday Report Generator
      </SectionTitle>
      
      <Container maxWidth="sm">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: 'blur(4px)',
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#667eea', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: '600' }}>
              Generating Birthday Report...
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Please wait while we fetch the data
            </Typography>
          </Box>
        </Backdrop>

        <Fade in={true} timeout={800}>
          <GlassCard>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Cake sx={{ fontSize: '3rem', color: '#667eea', mb: 1 }} />
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: '600',
                        color: '#1f2937',
                        mb: 1,
                      }}
                    >
                      Birthday Report
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b7280',
                        fontSize: '0.9rem',
                      }}
                    >
                      Generate and download Excel report of upcoming birthdays
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Label>
                    <CalendarToday sx={{ fontSize: '1.2rem' }} />
                    Days Left for Birthday
                  </Label>
                  <StyledTextField
                    fullWidth
                    value={days_left || ''}
                    type='number'
                    onChange={e => setDaysLeft(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder='Enter number of days (e.g., 7)'
                    inputProps={{ min: 0, max: 365 }}
                    InputProps={{
                      startAdornment: <CalendarToday sx={{ color: '#667eea', mr: 1 }} />,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <GradientButton
                    fullWidth
                    variant='contained'
                    size="large"
                    onClick={getData}
                    disabled={loading}
                    startIcon={loading ? null : <FileDownload />}
                  >
                    {loading ? 'Generating Report...' : 'Download Excel Report'}
                  </GradientButton>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          background: 'rgba(103, 126, 234, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          border: '1px solid rgba(103, 126, 234, 0.2)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#667eea',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            mb: 1,
                          }}
                        >
                          <Download sx={{ fontSize: '1rem' }} />
                          How it works:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            display: 'block',
                            lineHeight: 1.4,
                          }}
                        >
                          • Enter the number of days to look ahead for birthdays
                          <br />
                          • Click "Download Excel Report" to generate the file
                          <br />
                          • The Excel file will download automatically if data is found
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </Fade>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default Birthday;